import { useMemo, useState } from 'react'
import { fuelleVorlage, offenePlatzhalter, type Mailvorlage } from '@raporto/shared'
import { ActionButton } from './ActionButton.js'
import { notifyError } from './Toasts.js'

/**
 * Der Editor vor einem Serienversand.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum nicht einfach der MailEditor, dreissigmal
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil niemand dreissig Dialoge bestätigt. Der Serienversand der
 * Kita-Monatsrechnungen schickt am Monatsende an jede Familie eine eigene
 * Rechnung – bei einer mittleren Kita sind das zwanzig bis vierzig Mails. Ein
 * Editor je Empfänger hiesse vierzig Mal denselben Text lesen und vierzig Mal
 * auf «Senden» drücken. Was dabei herauskommt, ist kein geprüfter Versand,
 * sondern vierzig Mal blind geklickt – und dann ist der Editor schlimmer als
 * keiner, weil er Sorgfalt vortäuscht.
 *
 * Deshalb EIN Text für alle, und die Vereinzelung übernimmt der Server.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Die Platzhalter bleiben hier absichtlich stehen
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Im Einzelversand zeigt der Editor die fertige Nachricht – gefüllt, ohne
 * geschweifte Klammern. Hier geht das nicht: Es gibt nicht eine
 * Rechnungsnummer, sondern vierzig. Im Feld steht deshalb der Text MIT
 * `{{rechnungsnummer}}`, und gefüllt wird je Beleg beim Versand, aus den
 * Daten dieses Belegs.
 *
 * Damit das nicht zur Zumutung wird, steht die Vorschau daneben: Empfänger
 * wählen, und darunter steht die Nachricht, die genau diese Familie bekommt –
 * mit ihrer Nummer, ihrem Namen. Was in der Vorschau steht, geht hinaus.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was dieser Dialog NICHT tut
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Er sendet nichts von selbst. Der Knopf trägt die Anzahl, damit niemand
 * aus Versehen vierzig Mails auslöst, während er eine erwartet. Und er
 * schliesst sich nicht, wenn der Versand scheitert: Der geänderte Text bleibt
 * stehen, sonst ist die Arbeit zweimal verloren.
 */

export interface Serienempfaenger {
  /** Die Belegkennung – nur zur Unterscheidung in der Vorschau. */
  id: string
  /** Der Name, unter dem der Empfänger in der Liste erscheint. */
  anzeige: string
  /** Die Belegnummer dieses Empfängers, für `{{rechnungsnummer}}`. */
  nummer: string
  /** Der Kundenname dieses Empfängers, für `{{kundenname}}`. */
  kundenname: string
}

export function SerienMailEditor({
  titel,
  empfaenger,
  firmenname,
  vorlage,
  aufSenden,
  aufAbbrechen
}: {
  titel: string
  empfaenger: Serienempfaenger[]
  firmenname: string
  /** Die Vorlage MIT Platzhaltern – nicht die gefüllte Fassung. */
  vorlage: Mailvorlage
  /** Bekommt den Text mit Platzhaltern; gefüllt wird je Beleg auf dem Server. */
  aufSenden: (betreff: string, text: string) => Promise<void>
  aufAbbrechen: () => void
}): JSX.Element {
  const [betreff, setBetreff] = useState(vorlage.betreff)
  const [text, setText] = useState(vorlage.text)
  const [gezeigt, setGezeigt] = useState(empfaenger[0]?.id ?? '')

  const schau = empfaenger.find((e) => e.id === gezeigt) ?? empfaenger[0] ?? null

  /* Die Vorschau rechnet mit derselben Funktion, die der Server benutzt. */
  const vorschau = useMemo(() => {
    if (!schau) return { betreff, text }
    return fuelleVorlage(
      { betreff, text },
      { nummer: schau.nummer, firmenname, kundenname: schau.kundenname }
    )
  }, [betreff, text, schau, firmenname])

  /*
    Unbekannte Platzhalter fallen VOR dem Versand auf.

    Gemessen wird an der gefüllten Vorschau, nicht am Feld: `{{kundenname}}`
    im Feld ist richtig und verschwindet beim Füllen. Was danach noch in
    Klammern steht, kennt Raporto nicht – ein Tippfehler. Der Server weist es
    ebenfalls ab, aber erst beim ersten Beleg; hier steht es, bevor etwas
    hinausgeht.
  */
  const unbekannt = useMemo(
    () => [...new Set([...offenePlatzhalter(vorschau.betreff), ...offenePlatzhalter(vorschau.text)])],
    [vorschau]
  )

  const leer = betreff.trim() === '' || text.trim() === ''
  const anzahl = empfaenger.length

  return (
    <div className="o-dialog-grund" id="serienmail-editor">
      <div className="o-dialog o-dialog-breit">
        <h3>{titel}</h3>

        <p className="muted small" id="serienmail-anzahl">
          {anzahl === 1
            ? '1 Rechnung an 1 Empfänger.'
            : `${anzahl} Rechnungen an ${anzahl} Empfänger.`}{' '}
          Jede Familie bekommt ihre eigene Rechnung als PDF im Anhang.
        </p>

        <div className="o-field">
          <label htmlFor="serienmail-betreff">Betreff</label>
          <input
            id="serienmail-betreff"
            value={betreff}
            maxLength={200}
            autoComplete="off"
            onChange={(e) => setBetreff(e.target.value)}
          />
        </div>

        <div className="o-field">
          <label htmlFor="serienmail-text">Nachricht</label>
          <textarea
            id="serienmail-text"
            rows={12}
            value={text}
            maxLength={8000}
            onChange={(e) => setText(e.target.value)}
          />
          <span className="hint">
            Platzhalter werden je Empfänger ersetzt: <code>{'{{rechnungsnummer}}'}</code>,{' '}
            <code>{'{{kundenname}}'}</code>, <code>{'{{firmenname}}'}</code>. Sonst geht der Text so
            hinaus, wie er hier steht.
          </span>
        </div>

        {/*
          Die Vorschau. Sie ist nicht Zierde, sondern der Grund, dass man
          Platzhalter in einem Feld bearbeiten darf: Ohne sie müsste man sich
          vorstellen, was ankommt.
        */}
        <div className="o-field">
          <label htmlFor="serienmail-vorschau-wahl">Vorschau für</label>
          <select
            id="serienmail-vorschau-wahl"
            value={schau?.id ?? ''}
            onChange={(e) => setGezeigt(e.target.value)}
          >
            {empfaenger.map((e) => (
              <option key={e.id} value={e.id}>
                {e.anzeige} · {e.nummer}
              </option>
            ))}
          </select>
        </div>

        {/*
          `o-kasten` statt einer eigenen Klasse: Die Vorschau ist ein
          eingelassenes Feld wie jedes andere im Büro, und eine neue
          CSS-Regel für einen Dialog wäre eine Regel, die später niemand
          wiederfindet. Höhe begrenzt, damit ein langer Text den Dialog nicht
          über den Bildschirm hinaus schiebt.
        */}
        <div
          className="o-kasten"
          id="serienmail-vorschau"
          style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 12 }}
        >
          <p className="small" style={{ margin: '0 0 6px' }}>
            <strong>{vorschau.betreff}</strong>
          </p>
          <p className="small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            {vorschau.text}
          </p>
        </div>

        {unbekannt.length > 0 && (
          <p className="o-feldfehler" id="serienmail-platzhalter">
            Unbekannter Platzhalter: {unbekannt.join(', ')}. Bitte ersetzen – so geht die Nachricht
            nicht hinaus.
          </p>
        )}

        <div className="rowactions">
          <ActionButton
            id="btn-serienmail-senden"
            actionClass="B"
            disabled={leer || unbekannt.length > 0 || anzahl === 0}
            labels={{
              idle: anzahl === 1 ? '1 Rechnung senden' : `${anzahl} Rechnungen senden`,
              busy: 'Sendet …',
              success: 'Gesendet'
            }}
            onAction={async () => aufSenden(betreff, text)}
            onError={(err) => notifyError(err)}
          />
          <ActionButton
            variant="quiet"
            labels={{ idle: 'Abbrechen', busy: '…' }}
            onAction={async () => aufAbbrechen()}
          />
        </div>
      </div>
    </div>
  )
}
